import { signIn, signOut, restoreSession, AuthError } from './auth.js';
import { forgetConnection, rememberConnection, wasConnected } from './reconnect.js';
import { loadClasses } from './loadclasses.js';
import { loadStudy } from './loadstudy.js';
import { CLASSES, replaceClasses } from './classes.js';
import { validateAudioFile } from './validate.js';
import { uploadMemo, describeUploadError } from './upload.js';
import * as ui from './ui.js';
import * as screens from './screens.js';
import { clientIdFor, createConsent, createTokenStore, isConfigured, loadGis } from './gauth.js';

let config = null;
let session = null;
let username = null;
let pendingCourse = null;

/**
 * Google is a second consent, on top of the Cognito sign-in, and this is all
 * of it that lives outside gauth.js.
 *
 * The token sits in `googleTokens`, in this tab, and is not written anywhere
 * that outlives it -- see gauth.js for why that is worth one button press
 * after a reload. `consent` is built lazily, because a site with no client id
 * configured must not go and fetch Google's script for nothing.
 */
const googleTokens = createTokenStore();
let consent = null;

async function googleToken(interactive) {
  const live = googleTokens.get();
  if (live) return live;

  const clientId = clientIdFor(config);
  if (!clientId) throw new Error('Google Calendar is not set up for this site yet.');

  if (!consent) consent = createConsent(clientId, await loadGis());
  // 'consent' shows the dialog, '' asks silently. A token that quietly expires
  // mid-session is renewed without the student being interrupted for it.
  const token = googleTokens.set(await consent.request(interactive ? 'consent' : ''));
  if (!token) throw new Error('Google did not return an access token.');
  return token;
}

/** localStorage, or nothing. Reaching for it throws when cookies are blocked. */
function store() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

const calendarAccess = {
  get configured() {
    return isConfigured(config);
  },
  get connected() {
    return googleTokens.peek() !== null;
  },
  /**
   * Whether this browser has ever said yes to Google. One bit, not a token --
   * see reconnect.js for why that is the whole trick.
   */
  get remembered() {
    return wasConnected(store());
  },
  getToken: googleToken,
  /**
   * The calendar back without a dialog.
   *
   * `prompt: ''` is answered silently once consent is on record, so a reload
   * costs a round trip rather than a button press. Returns false rather than
   * throwing when Google wants to see the student again -- an expired consent
   * is an ordinary Connect button, not an error worth a red line.
   */
  async reconnect() {
    try {
      return Boolean(await googleToken(false));
    } catch {
      return false;
    }
  },
  remember: () => rememberConnection(store()),
  forget: () => googleTokens.clear(),
  revoke() {
    // Handed back to Google as well as dropped here, so disconnecting means
    // disconnected rather than merely forgotten by this tab.
    consent?.revoke(googleTokens.peek());
    googleTokens.clear();
    // And stop trying to reconnect silently on the next load: disconnecting
    // must mean disconnected tomorrow as well as now.
    forgetConnection(store());
  },
};

async function boot() {
  try {
    const response = await fetch('./config.json', { cache: 'no-store' });
    if (!response.ok) throw new Error(`config.json returned ${response.status}`);
    config = await response.json();
  } catch {
    ui.showLoginError('Could not load configuration. Reload the page.');
    return;
  }
  bind();

  // The tokens have been in localStorage all along -- amazon-cognito-identity-js
  // puts them there -- and this page never looked, which is the whole reason
  // the form appeared every morning. The refresh token is good for 30 days, so
  // ordinary daily use never sees it again. A failed restore is not an error
  // worth saying out loud: it is a login screen.
  const restored = await restoreSession(config);
  if (restored) enterApp(restored, restored.username);
  else ui.showLogin();
}

/**
 * Everything that has to be true once there is a session, whether it came from
 * the form or from the last time this device signed in.
 *
 * One function rather than two so the restored path cannot quietly drift from
 * the typed one -- a sidebar that only mounts after a manual sign-in is the
 * kind of difference nobody notices until the morning it matters.
 */
function enterApp(newSession, user) {
  session = newSession;
  username = user;
  ui.showApp();
  // Google is a separate consent and is not granted by signing in here. All
  // this hands over is the ability to ask for one.
  screens.setCalendarAccess(calendarAccess);
  // After the app is up, not before. Canvas being down must not hold the
  // sign-in open or cost you the upload page, which is what this site is
  // for and which works perfectly well without Canvas.
  refreshClasses();
}

function bind() {
  ui.el('login-form').addEventListener('submit', onSignIn);
  ui.el('sign-out').addEventListener('click', onSignOut);
  ui.el('upload-button').addEventListener('click', openClassPick);
  ui.el('class-pick-cancel').addEventListener('click', closeClassPick);
  ui.el('class-pick-list').addEventListener('click', onClassPicked);
  ui.el('file-input').addEventListener('change', onFileChosen);
  ui.el('note-open').addEventListener('click', ui.showNote);
  ui.el('note-back').addEventListener('click', ui.showHome);

  // Delegated: the class rows do not exist until mountSidebar runs.
  ui.el('side-nav').addEventListener('click', onNavClick);

  ui.el('study-back').addEventListener('click', screens.backToClass);
  ui.el('done-back').addEventListener('click', screens.backToClass);
  ui.el('study-shuffle').addEventListener('click', screens.shuffle);
  ui.el('do-flip').addEventListener('click', screens.flip);
  ui.el('mark-known').addEventListener('click', screens.markKnown);
  ui.el('mark-forgot').addEventListener('click', screens.markForgot);
  ui.el('review-forgot').addEventListener('click', screens.reviewForgot);
  ui.el('restart-set').addEventListener('click', screens.restart);

  ui.el('notepage-back').addEventListener('click', screens.backToClassFromNote);
  ui.el('notepage-title').addEventListener('input', screens.onNoteEdited);
  ui.el('notepage-body').addEventListener('input', screens.onNoteEdited);

  const stage = ui.el('card-stage');
  stage.addEventListener('pointerdown', screens.onCardPointerDown);
  stage.addEventListener('pointermove', screens.onCardPointerMove);
  stage.addEventListener('pointerup', screens.onCardPointerUp);
  stage.addEventListener('pointercancel', screens.onCardPointerCancel);
  stage.addEventListener('keydown', screens.onCardKeyDown);

  ui.el('cal-connect-btn').addEventListener('click', screens.connectCalendar);
  ui.el('cal-disconnect').addEventListener('click', screens.disconnectCalendar);
  ui.el('cal-views').addEventListener('click', (event) => {
    const button = event.target.closest('.seg-btn');
    if (button) screens.setCalendarView(button.dataset.view);
  });

  ui.el('agenda-views').addEventListener('click', (event) => {
    const button = event.target.closest('.seg-btn');
    if (button) screens.showAgenda(button.dataset.view);
  });

  screens.mountSidebar();
  // A sidebar that fits at one width clips at another, and the class names
  // are the only thing on the page whose overflow is not obvious.
  window.addEventListener('resize', screens.measureLabels);
}

function onNavClick(event) {
  const row = event.target.closest('.nav-row');
  if (!row) return;

  const target = row.dataset.nav;
  if (target === 'home') ui.showHome();
  else if (target === 'note') ui.showNote();
  else if (target === 'calendar') screens.openCalendar();
  else if (target.startsWith('class-')) screens.openClass(Number(target.slice(6)));
}

/**
 * The class is chosen before the file, because it is the one thing nothing
 * downstream can recover. No classes means no upload: a recording that
 * belongs to nothing has no folder to land in and nothing to be asked about.
 */
function openClassPick() {
  if (CLASSES.length === 0) {
    ui.setStatus('No Canvas classes yet, so there is nowhere to file a recording.', 'error');
    return;
  }

  const list = ui.el('class-pick-list');
  list.replaceChildren();

  CLASSES.forEach((cls, index) => {
    const row = ui.clone('tpl-class-pick');
    row.dataset.class = String(index);
    row.querySelector('.nav-dot').style.background = cls.color;
    row.querySelector('.class-pick-name').textContent = cls.name;
    list.append(row);
  });

  ui.el('class-pick').hidden = false;
}

function closeClassPick() {
  ui.el('class-pick').hidden = true;
}

function onClassPicked(event) {
  const row = event.target.closest('.class-pick-row');
  if (!row) return;
  pendingCourse = CLASSES[Number(row.dataset.class)];
  if (!pendingCourse) return;
  closeClassPick();
  ui.el('file-input').click();
}

async function onSignIn(event) {
  event.preventDefault();
  const submit = ui.el('login-submit');
  const user = ui.el('login-username').value.trim();
  const pass = ui.el('login-password').value;

  if (!user || !pass) {
    ui.showLoginError('Enter your username and password.');
    return;
  }

  ui.showLoginError('');
  submit.disabled = true;
  submit.textContent = 'Signing in…';

  try {
    const signedIn = await signIn(config, user, pass);
    ui.el('login-password').value = '';
    enterApp(signedIn, user);
  } catch (err) {
    ui.showLoginError(err instanceof AuthError ? err.friendly : 'Could not sign in.');
  } finally {
    submit.disabled = false;
    submit.textContent = 'Sign in';
  }
}

/**
 * Fill the sidebar from Canvas.
 *
 * Deliberately not awaited by the caller and deliberately unable to throw: a
 * Canvas outage is a smaller thing than being signed out, and the recording
 * upload does not need Canvas at all.
 */
async function refreshClasses() {
  const result = await loadClasses(config, session);
  screens.mountSidebar();
  if (!result.ok || result.count === 0) {
    // Said once, on the screen they are already looking at. An expired token
    // carries no retry, because the fix is on a machine that is not this one.
    ui.setStatus(result.message, result.ok ? null : 'error');
  }

  // Joins on course code, so it has to wait for the classes above to exist.
  //
  // A count of zero is still not surfaced: a class with no material yet
  // already reads as empty on its own screen, the same honest empty state
  // classes.js gives it before Canvas answers, and that must not overwrite
  // whatever loadClasses just said about Canvas itself.
  //
  // A *failure* is surfaced (2026-08-13). Those two were treated alike, and
  // they are not alike: "nothing has been made yet" and "your material is
  // there and this page could not read it" looked identical on screen, so a
  // published set that never loaded was indistinguishable from one that was
  // never made. Only shown when `loadClasses` had nothing of its own to say,
  // so the Canvas message still wins the one status line.
  const study = await loadStudy(config, session, CLASSES);
  if (!study.ok && result.ok && result.count > 0) {
    ui.setStatus(study.message, 'error');
  }

  // The agenda joins course ids to the sidebar's colours, so it is drawn
  // after the classes land rather than racing them.
  screens.setAgendaSource(config, session);
  screens.showAgenda();
}

function onSignOut() {
  signOut(config, username);
  replaceClasses([]);
  // Signing out of Alter hands the Google token back to Google as well. The
  // two grants are separate, but leaving one alive on a machine somebody just
  // signed out of would make them separate in the wrong direction.
  calendarAccess.revoke();
  screens.setCalendarAccess(null);
  screens.setAgendaSource(null, null);
  screens.mountSidebar();
  session = null;
  username = null;
  // The class-pick panel lives inside home-view, so showLogin() below hides
  // its ancestor but leaves it unhidden underneath -- a re-sign-in would put
  // it straight back on screen holding rows indexed into the class list this
  // user is about to replace. A recording filed against a stale index cannot
  // be recovered downstream, so it is closed here rather than left to chance.
  closeClassPick();
  pendingCourse = null;
  ui.setStatus('', null);
  ui.setProgress(null);
  ui.showLogin();
}

async function onFileChosen(event) {
  const file = event.target.files?.[0];
  event.target.value = ''; // so re-picking the same file fires change again
  if (!file) return;

  const course = pendingCourse;
  pendingCourse = null;
  if (!course) return; // the dialog was opened some other way

  const check = validateAudioFile(file);
  if (!check.ok) {
    ui.setStatus(check.reason, 'error');
    return;
  }

  ui.el('upload-button').disabled = true;
  ui.setStatus(`Uploading ${file.name}…`, null);
  ui.setProgress(0);

  try {
    await uploadMemo({
      config,
      session,
      file,
      course,
      onProgress: (pct) => ui.setProgress(pct),
    });
    ui.setProgress(100);
    ui.setStatus(`Uploaded ${file.name} for ${course.meta || course.name}.`, 'done');
    ui.addCapture(file);
  } catch (err) {
    ui.setStatus(describeUploadError(err), 'error');
  } finally {
    ui.el('upload-button').disabled = false;
    setTimeout(() => ui.setProgress(null), 1200);
  }
}

boot();
